import { OperatingHours } from 'src/interfaces/interfaces';
import { Purchase, Reward, Streak } from 'src/queries/mappers';
import {
  Bucket,
  DateRange,
  bucketKey,
  dayCount,
  enumerateBuckets,
  isWithin,
  startOfDay
} from './period';

/**
 * Every derivation the analytics page performs on `GET loyalty/stats`.
 *
 * The endpoint returns the location's full history in one payload — all
 * purchases, rewards and streaks — so period filtering and every aggregate
 * happen here rather than in a round trip. Keeping them out of the chart
 * components is what lets those components stay presentational.
 */

export interface SeriesPoint {
  key: string;
  stamps: number;
  issued: number;
  redeemed: number;
}

/** Stamps and rewards per bucket, including buckets with no activity. */
export const buildSeries = (
  purchases: Purchase[],
  rewards: Reward[],
  range: DateRange,
  bucket: Bucket
): SeriesPoint[] => {
  const points = new Map<string, SeriesPoint>();
  for (const key of enumerateBuckets(range, bucket)) {
    points.set(key, { key, stamps: 0, issued: 0, redeemed: 0 });
  }

  for (const purchase of purchases) {
    if (!isWithin(purchase.date, range)) continue;
    const point = points.get(bucketKey(new Date(purchase.date), bucket));
    if (point) point.stamps += 1;
  }

  for (const reward of rewards) {
    if (isWithin(reward.dateCreated, range)) {
      const point = points.get(bucketKey(new Date(reward.dateCreated), bucket));
      if (point) point.issued += 1;
    }
    // A reward issued in one period and redeemed in another counts against the
    // period it was actually redeemed in, so redemptions track footfall.
    if (reward.redeemed && isWithin(reward.dateRedeemed, range)) {
      const point = points.get(bucketKey(new Date(reward.dateRedeemed), bucket));
      if (point) point.redeemed += 1;
    }
  }

  return [...points.values()];
};

export interface PeriodTotals {
  stamps: number;
  issued: number;
  redeemed: number;
  /** Customers who collected at least one stamp in the period. */
  customers: number;
  /** Customers whose first stamp anywhere in this location's history is in the period. */
  newCustomers: number;
}

/** First-stamp date per customer, across the whole history. */
const firstStampByCustomer = (purchases: Purchase[]) => {
  const first = new Map<number, number>();
  for (const purchase of purchases) {
    const time = new Date(purchase.date).getTime();
    if (Number.isNaN(time)) continue;
    const existing = first.get(purchase.userId);
    if (existing === undefined || time < existing) first.set(purchase.userId, time);
  }
  return first;
};

export const totalsFor = (
  purchases: Purchase[],
  rewards: Reward[],
  range: DateRange,
  firstStamps = firstStampByCustomer(purchases)
): PeriodTotals => {
  const customers = new Set<number>();
  let stamps = 0;

  for (const purchase of purchases) {
    if (!isWithin(purchase.date, range)) continue;
    stamps += 1;
    customers.add(purchase.userId);
  }

  let newCustomers = 0;
  for (const time of firstStamps.values()) {
    if (time >= range.start.getTime() && time <= range.end.getTime()) newCustomers += 1;
  }

  let issued = 0;
  let redeemed = 0;
  for (const reward of rewards) {
    if (isWithin(reward.dateCreated, range)) issued += 1;
    if (reward.redeemed && isWithin(reward.dateRedeemed, range)) redeemed += 1;
  }

  return { stamps, issued, redeemed, customers: customers.size, newCustomers };
};

/** Per-bucket unique customers, for the customers tile's sparkline. */
export const customersSeries = (
  purchases: Purchase[],
  range: DateRange,
  bucket: Bucket
): number[] => {
  const perBucket = new Map<string, Set<number>>();
  for (const key of enumerateBuckets(range, bucket)) perBucket.set(key, new Set());

  for (const purchase of purchases) {
    if (!isWithin(purchase.date, range)) continue;
    perBucket.get(bucketKey(new Date(purchase.date), bucket))?.add(purchase.userId);
  }

  return [...perBucket.values()].map((set) => set.size);
};

export interface CumulativePoint {
  key: string;
  total: number;
}

/**
 * Lifetime stamp total at the end of each bucket, clipped to the visible
 * window. Stamps collected before the range still count toward the total — the
 * line shows how the location's all-time figure grew, so it never restarts at
 * zero when the period changes.
 */
export const cumulativeSeries = (
  purchases: Purchase[],
  range: DateRange,
  bucket: Bucket
): CumulativePoint[] => {
  const times = purchases
    .map((purchase) => new Date(purchase.date).getTime())
    .filter((time) => !Number.isNaN(time))
    .sort((a, b) => a - b);

  const keys = enumerateBuckets(range, bucket);
  const points: CumulativePoint[] = [];
  let cursor = 0;

  keys.forEach((key, index) => {
    // A bucket's boundary is the start of the next one, or the range's end for
    // the final bucket, which may be mid-bucket.
    const nextKey = keys[index + 1];
    const boundary = nextKey
      ? startOfDay(new Date(`${nextKey}T00:00:00`)).getTime()
      : range.end.getTime() + 1;

    while (cursor < times.length && times[cursor] < boundary) cursor += 1;
    points.push({ key, total: cursor });
  });

  return points;
};

export interface SpreadBucket {
  label: string;
  customers: number;
}

const SPREAD_BUCKETS: { label: string; min: number; max: number }[] = [
  { label: '1–5 stamps', min: 1, max: 5 },
  { label: '6–15 stamps', min: 6, max: 15 },
  { label: '16–30 stamps', min: 16, max: 30 },
  { label: '31–50 stamps', min: 31, max: 50 },
  { label: '51+ stamps', min: 51, max: Infinity }
];

/**
 * How many customers sit in each lifetime-stamp band. Deliberately not period
 * filtered: it answers "how loyal is my base", which is a standing question
 * about the whole customer list, not about a window.
 */
export const loyaltySpread = (purchases: Purchase[]): SpreadBucket[] => {
  const perCustomer = new Map<number, number>();
  for (const purchase of purchases) {
    perCustomer.set(purchase.userId, (perCustomer.get(purchase.userId) ?? 0) + 1);
  }

  return SPREAD_BUCKETS.map((band) => ({
    label: band.label,
    customers: [...perCustomer.values()].filter(
      (count) => count >= band.min && count <= band.max
    ).length
  }));
};

export interface TimeOfDayPoint {
  /** `HH:MM` at the start of the half-hour slot. */
  slot: string;
  stamps: number;
  /** Average stamps in this slot per day in the period. */
  perDay: number;
}

const SLOT_MINUTES = 30;

/** `08:00` out of the API's `1970-01-01T08:00:00.000Z` shaped times. */
const minutesFromTimeString = (value: string): number | null => {
  const match = /T(\d{2}):(\d{2})/.exec(value);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

const formatSlot = (minutes: number) =>
  `${`${Math.floor(minutes / 60)}`.padStart(2, '0')}:${`${minutes % 60}`.padStart(2, '0')}`;

/**
 * Stamps by time of day, over the location's widest opening window. Falls back
 * to the hours stamps were actually collected in when opening hours are not
 * configured, so the chart is never a flat line across a 24-hour axis.
 */
export const timeOfDaySeries = (
  purchases: Purchase[],
  range: DateRange,
  operatingHours: OperatingHours[] | null
): TimeOfDayPoint[] => {
  const inRange = purchases.filter((purchase) => isWithin(purchase.date, range));

  const opens = (operatingHours ?? [])
    .map((hours) => minutesFromTimeString(hours.openTime))
    .filter((value): value is number => value !== null);
  const closes = (operatingHours ?? [])
    .map((hours) => minutesFromTimeString(hours.closeTime))
    .filter((value): value is number => value !== null);

  let windowStart: number;
  let windowEnd: number;

  if (opens.length && closes.length) {
    windowStart = Math.min(...opens);
    windowEnd = Math.max(...closes);
  } else if (inRange.length) {
    const minutes = inRange.map((purchase) => {
      const date = new Date(purchase.date);
      return date.getHours() * 60 + date.getMinutes();
    });
    windowStart = Math.min(...minutes);
    windowEnd = Math.max(...minutes);
  } else {
    windowStart = 6 * 60;
    windowEnd = 20 * 60;
  }

  // Round outward to whole slots and give a degenerate window some width.
  windowStart = Math.floor(windowStart / SLOT_MINUTES) * SLOT_MINUTES;
  windowEnd = Math.ceil(windowEnd / SLOT_MINUTES) * SLOT_MINUTES;
  if (windowEnd <= windowStart) windowEnd = Math.min(windowStart + 12 * 60, 24 * 60);

  const counts = new Map<string, number>();
  for (let minutes = windowStart; minutes <= windowEnd; minutes += SLOT_MINUTES) {
    counts.set(formatSlot(minutes % (24 * 60)), 0);
  }

  for (const purchase of inRange) {
    const date = new Date(purchase.date);
    const minutes = date.getHours() * 60 + date.getMinutes();
    const slot = formatSlot(Math.floor(minutes / SLOT_MINUTES) * SLOT_MINUTES);
    // Stamps outside opening hours still happened; fold them into the nearest
    // edge slot rather than dropping them from the total.
    if (counts.has(slot)) {
      counts.set(slot, (counts.get(slot) ?? 0) + 1);
    } else {
      const edge = minutes < windowStart ? formatSlot(windowStart) : formatSlot(windowEnd % (24 * 60));
      counts.set(edge, (counts.get(edge) ?? 0) + 1);
    }
  }

  const days = dayCount(range);
  return [...counts.entries()].map(([slot, stamps]) => ({
    slot,
    stamps,
    perDay: stamps / days
  }));
};

export interface StreakBin {
  streak: number;
  customers: number;
}

/**
 * Customers at each rung of the streak ladder, 1 up to the target. Zero-streak
 * customers are excluded: the question is how close active customers are to a
 * reward, and the lapsed majority would flatten every other bar.
 */
export const streakBins = (streaks: Streak[], streakTarget: number): StreakBin[] => {
  const top = Math.max(1, Math.min(streakTarget || 0, 30));
  const bins = new Map<number, number>();
  for (let rung = 1; rung <= top; rung += 1) bins.set(rung, 0);

  for (const streak of streaks) {
    const rung = Math.min(streak.currentStreak, top);
    if (rung >= 1) bins.set(rung, (bins.get(rung) ?? 0) + 1);
  }

  return [...bins.entries()].map(([streak, customers]) => ({ streak, customers }));
};

/** Oldest stamp or reward on record, used as the start of the all-time range. */
export const earliestEvent = (purchases: Purchase[], rewards: Reward[]): Date | undefined => {
  const times = [
    ...purchases.map((purchase) => new Date(purchase.date).getTime()),
    ...rewards.map((reward) => new Date(reward.dateCreated).getTime())
  ].filter((time) => !Number.isNaN(time));

  return times.length ? new Date(Math.min(...times)) : undefined;
};

export { firstStampByCustomer };
